import apiClient from './apiClient';

const API_URL = '/tramites';

/**
 * Valor del filtro de carpeta que significa "los que no tienen ninguna asignada".
 *
 * Tiene que existir un centinela porque null/'' ya quieren decir "sin filtrar", y "sin archivar"
 * es una búsqueda real del estudio: son los trámites cuyo papel todavía no se guardó en ninguna
 * carpeta. Debe coincidir con TramiteService.SIN_CARPETA (Java).
 */
export const SIN_CARPETA = -1;

const normalizeItems = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (payload && Array.isArray(payload.content)) return payload.content;
    if (payload && Array.isArray(payload.data)) return payload.data;
    return [];
};

const adaptTramiteItem = (t = {}) => {
    const nombreCliente = t.nombreCliente ?? t.cliente?.nombre ?? '';
    const apellidoCliente = t.apellidoCliente ?? t.cliente?.apellido ?? '';
    const cuilCliente = t.cuilCliente ?? t.idCliente ?? t.cliente?.cuil ?? null;
    const idTipoTramite = t.idTipoTramite ?? t.tipoTramite?.idTipoTramite ?? null;
    const tipoTramiteNombre = t.tipoTramite ?? t.nombreTipoTramite ?? t.tipoTramite?.nombre ?? null;

    return {
        ...t,
        // Compatibilidad con contratos viejos
        idCliente: t.idCliente ?? cuilCliente,
        idTipoTramite,
        nombreTipoTramite: t.nombreTipoTramite ?? tipoTramiteNombre,
        idEstado: t.idEstado ?? t.idEstadoActual ?? null,
        cliente: t.cliente ?? (cuilCliente ? {
            cuil: cuilCliente,
            nombre: nombreCliente,
            apellido: apellidoCliente
        } : null),
        tipoTramite: tipoTramiteNombre,
    };
};

const adaptTramitesPayload = (payload) => {
    const items = normalizeItems(payload).map(adaptTramiteItem);
    if (Array.isArray(payload)) return items;
    return {
        ...(payload || {}),
        content: items,
    };
};

// Obtener trámites paginados con filtros servidor
export const getTramites = async (...args) => {
    let legacyMode = args.length === 0;

    let [
        page = 0,
        size = 15,
        idCliente = '',
        idTipoTramite = null,
        idEstadoActual = null,
        nombreApellido = '',
        organismo = null,
        // Carpeta física del archivo. SIN_CARPETA (-1) trae justamente los que no tienen ninguna.
        idCarpeta = null
    ] = args;

    if (legacyMode) {
        page = 0;
        size = 2000;
        idCliente = '';
        idTipoTramite = null;
        idEstadoActual = null;
        nombreApellido = '';
    }

    // Retrocompatibilidad con la firma previa: getTramites(idCliente, idTipoTramite, idEstadoActual)
    if (typeof page !== 'number' || page === null) {
        legacyMode = true;
        const oldIdCliente = page;
        const oldIdTipoTramite = size;
        const oldIdEstadoActual = idCliente;

        page = 0;
        size = 2000;
        idCliente = oldIdCliente || '';
        idTipoTramite = oldIdTipoTramite;
        idEstadoActual = oldIdEstadoActual;
        nombreApellido = '';
    }

    const params = { page, size, sort: 'fechaEstadoActual,desc' };

    if (idCliente && String(idCliente).trim() !== '') params.cuil = String(idCliente).trim();
    if (idTipoTramite !== null && idTipoTramite !== '' && idTipoTramite !== undefined) params.idTipoTramite = idTipoTramite;
    if (idEstadoActual !== null && idEstadoActual !== '' && idEstadoActual !== undefined) params.idEstado = idEstadoActual;
    if (nombreApellido && String(nombreApellido).trim() !== '') params.nombreApellido = String(nombreApellido).trim();
    if (organismo && String(organismo).trim() !== '') params.organismo = String(organismo).trim();
    if (idCarpeta !== null && idCarpeta !== '' && idCarpeta !== undefined) params.idCarpeta = idCarpeta;

    const response = await apiClient.get(API_URL, { params });
    const adapted = adaptTramitesPayload(response.data);

    // Compatibilidad con pantallas legacy que esperan un array plano.
    if (legacyMode) {
        return normalizeItems(adapted);
    }

    return adapted;
};

/**
 * Todos los trámites para Estadísticas de trámites, sin las carpetas viejas que creó la migración
 * del Excel: nacen con la fecha de la importación y contarían como trámites nuevos de ese mes.
 */
export const getTramitesParaEstadisticas = async () => {
    const params = { page: 0, size: 2000, sort: 'fechaEstadoActual,desc', excluirMigrados: true };
    const response = await apiClient.get(API_URL, { params });
    return normalizeItems(adaptTramitesPayload(response.data));
};

/**
 * Listado "A liquidar" (sección Listados): las carpetas armadas que todavía no se presentaron.
 *
 * Tiene endpoint propio (/tramites/a-liquidar) y no filtros sueltos sobre el listado general:
 * qué carpeta está "esperando" no es un filtro, es un criterio por ámbito que vive en
 * ListadoALiquidarService — igual que "Para aprobar".
 *
 * Trae hasta 200 filas de una: el listado son ~100 carpetas, y la tabla las agrupa por mes del
 * lado del cliente. Paginar de a 25 partiría un mes al medio entre dos páginas y el bloque
 * "Meses anteriores" mostraría solo una parte de lo vencido.
 */
export const getTramitesParaLiquidar = async (mes = '', page = 0, size = 200, texto = '', idTipoTramite = '',
                                              revisarFecha = false) => {
    const params = { page, size };
    // Solo las carpetas que vinieron de la planilla sin fecha: la revisión de la migración.
    if (revisarFecha) params.revisarFecha = true;
    const agregar = (clave, valor) => {
        const limpio = String(valor ?? '').trim();
        if (limpio !== '') params[clave] = limpio;
    };
    agregar('mes', mes);
    agregar('idTipoTramite', idTipoTramite);
    agregar('texto', texto);

    const response = await apiClient.get(`${API_URL}/a-liquidar`, { params });
    return response.data;
};

/**
 * Registra el control de una carpeta a liquidar: marcarla controlada, la nota y/o la nueva fecha
 * de inicio. Las tres cosas van juntas porque en el escritorio son un solo gesto, y las tres
 * dejan constancia en el historial de estados del trámite (ver ListadoALiquidarService).
 *
 * @param {{marcarControlada?: boolean, fechaControl?: string, nota: string,
 *          nuevaFechaInicio?: string, iniciaAhora?: boolean}} datos
 */
export const registrarControlLiquidar = async (idTramite, datos) => {
    const response = await apiClient.patch(`${API_URL}/${idTramite}/control-liquidar`, datos);
    return response.data;
};

/**
 * Listado "Para aprobar" (sección Listados): los trámites presentados que esperan que el
 * organismo resuelva. Es el tramo siguiente a "A liquidar" en el circuito del estudio:
 * a liquidar → turno ANSES → para aprobar → cobranzas.
 *
 * A diferencia de "A liquidar", acá el filtro NO es un estado único: qué significa "esperando"
 * cambia según el ámbito (RTI abre toda la cadena de apelaciones y pericias, reajuste habla de
 * reclamo administrativo). Esa traducción vive en el backend, ver ListadoParaAprobarService.
 *
 * El backend acepta un solo `texto` y decide solo si es CUIL o nombre, así que acá no hay que
 * separarlo como en getTramitesParaLiquidar.
 */
export const getTramitesParaAprobar = async (filtros = {}, page = 0, size = 25, texto = '') => {
    const { grupo = '', idTipoTramite = '', desde = '', hasta = '' } = filtros;
    const params = { page, size };
    const agregar = (clave, valor) => {
        const limpio = String(valor ?? '').trim();
        if (limpio !== '') params[clave] = limpio;
    };
    agregar('grupo', grupo);
    agregar('idTipoTramite', idTipoTramite);
    // Acotan la fecha de INGRESO A ANSES, no la del último cambio de estado.
    agregar('desde', desde);
    agregar('hasta', hasta);
    agregar('texto', texto);

    const response = await apiClient.get(`${API_URL}/para-aprobar`, { params });
    return response.data;
};

/** Los grupos del listado "Para aprobar" ({ambito, etiqueta}), tal como los ordena la planilla. */
export const getGruposParaAprobar = async () => {
    const response = await apiClient.get(`${API_URL}/para-aprobar/grupos`);
    return response.data || [];
};

// Tablero de "Para aprobar" (V245): todas las filas con su estado en ANSES, último control y
// alerta, más el estado del robot y las secciones. Sin paginar: son cientos de filas y se agrupan
// del lado del cliente.
export const getTableroParaAprobar = async (texto = '') => {
    const params = {};
    if (String(texto || '').trim() !== '') params.texto = String(texto).trim();
    const response = await apiClient.get(`${API_URL}/para-aprobar/tablero`, { params });
    return response.data;
};

// Un usuario controló el expediente. datos = { resultado, etapa, fechaResolucion, observacion,
// nroExpediente, fechaIngresoAnses }.
export const registrarControlExpediente = async (idTramite, datos) => {
    await apiClient.post(`${API_URL}/para-aprobar/${idTramite}/controles`, datos);
};

// Confirmar una novedad. datos = { resultado, accion, fechaResolucion, observacion }; accion:
// PASAR_A_FAVORABLE | CERRAR_DESFAVORABLE | SIGUE_EN_LISTADO | CARGAR_CITACION.
export const confirmarNovedadExpediente = async (idTramite, datos) => {
    await apiClient.post(`${API_URL}/para-aprobar/${idTramite}/confirmar`, datos);
};

// Expedientes de ANSES del trámite (V265): un trámite puede tener varios. Cada llamada devuelve la
// lista completa: [{ id, nroExpediente, fechaIngreso, descripcion, origen (CARGADO|TURNO), principal }].
export const getExpedientesAnses = async (idTramite) =>
    (await apiClient.get(`${API_URL}/${idTramite}/expedientes-anses`)).data;
export const agregarExpedienteAnses = async (idTramite, datos) =>
    (await apiClient.post(`${API_URL}/${idTramite}/expedientes-anses`, datos)).data;
export const editarExpedienteAnses = async (idTramite, idExpediente, datos) =>
    (await apiClient.put(`${API_URL}/${idTramite}/expedientes-anses/${idExpediente}`, datos)).data;
// Seguir o no un expediente (V266): si no se sigue, no avisa ni cuenta en Para aprobar. Es la misma
// marca que en Seguimiento de Expedientes (por cliente y expediente).
export const seguirExpedienteAnses = async (idTramite, nroExpediente, seguir) =>
    (await apiClient.put(`${API_URL}/${idTramite}/expedientes-anses/seguir`, { nroExpediente, seguir })).data;
export const eliminarExpedienteAnses = async (idTramite, idExpediente) =>
    (await apiClient.delete(`${API_URL}/${idTramite}/expedientes-anses/${idExpediente}`)).data;

/** Seguir (o dejar de seguir) un trámite: a quien lo sigue le llegan los avisos de ANSES de su expediente. */
export const seguirTramiteParaAprobar = async (idTramite, seguir) => {
    const url = `${API_URL}/para-aprobar/${idTramite}/seguir`;
    const resp = seguir ? await apiClient.put(url) : await apiClient.delete(url);
    return resp.data;
};

/** Seguir o dejar de seguir varios trámites de una vez ("Seguir varios" por sección). */
export const seguirVariosParaAprobar = async (idsTramite, seguir) =>
    (await apiClient.put(`${API_URL}/para-aprobar/seguir-varios`, { idsTramite, seguir })).data;

export const cambiarSincronizacionParaAprobar = async (ambito, sincronizar) => {
    const response = await apiClient.put(`${API_URL}/para-aprobar/sincronizacion/${ambito}`, { sincronizar });
    return response.data;
};

export const getResumenTramites = async (idCliente = '', nombreApellido = '', idTipoTramite = null, organismo = null, idCarpeta = null) => {
    const params = {};

    if (idCliente && String(idCliente).trim() !== '') params.cuil = String(idCliente).trim();
    if (nombreApellido && String(nombreApellido).trim() !== '') params.nombreApellido = String(nombreApellido).trim();
    if (idTipoTramite !== null && idTipoTramite !== '' && idTipoTramite !== undefined) params.idTipoTramite = idTipoTramite;
    if (organismo && String(organismo).trim() !== '') params.organismo = String(organismo).trim();
    // El resumen respeta los mismos filtros que el listado: si no, los contadores de arriba
    // contradicen a la tabla de abajo.
    if (idCarpeta !== null && idCarpeta !== '' && idCarpeta !== undefined) params.idCarpeta = idCarpeta;

    const response = await apiClient.get(`${API_URL}/resumen`, { params });
    return response.data;
};


// Crear un nuevo trámite
export const crearTramite = async (tramite) => {
    const response = await apiClient.post(API_URL, tramite);
    return response.data;
};

// Buscar trámite por su ID
export const getTramitePorId = async (idTramite) => {
    const response = await apiClient.get(`${API_URL}/${idTramite}`);
    return response.data;
};

// Modificar un trámite existente
export const modificarTramite = async (idTramite, tramite) => {
    const response = await apiClient.put(`${API_URL}/${idTramite}`, tramite);
    return response.data;
};

// Actualiza solo la ubicación de la carpeta y el inicio diferido (marca + fecha estimada).
// Update acotado: no toca el resto del payload del trámite.
export const actualizarLogisticaTramite = async (idTramite, logistica) => {
    const response = await apiClient.patch(`${API_URL}/${idTramite}/logistica`, logistica);
    return response.data;
};

// Actualiza solo los flags del checklist del trámite (DNI, Pacto, Carátula).
// A diferencia de modificarTramite, no manda `version`: el endpoint no valida
// concurrencia porque es un toggle independiente del resto de los datos.
export const actualizarChecklistTramite = async (idTramite, checklist) => {
    const response = await apiClient.patch(`${API_URL}/${idTramite}/checklist`, checklist);
    return response.data;
};

// Actualiza los datos del pacto de honorarios (haberes / 30% / pactoOk). Mismo criterio que
// actualizarChecklistTramite: sin control de versión, campos independientes del resto del trámite.
export const actualizarPactoTramite = async (idTramite, pacto) => {
    const response = await apiClient.patch(`${API_URL}/${idTramite}/pacto`, pacto);
    return response.data;
};

const escapeRegExp = (value = '') => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const esErrorReintentableConcurrencia = (error) => {
    const status = error?.response?.status;
    const rawData = error?.response?.data;
    const mensaje = typeof rawData === 'string'
        ? rawData
        : (rawData?.message || rawData?.error || '');
    const normalizado = String(mensaje || '').toLowerCase();

    return status === 409
        || (status >= 500 && status < 600)
        || normalizado.includes('modificado por otro usuario')
        || normalizado.includes('optimistic');
};

const componerComentariosConMotivo = ({ comentariosBase = '', comentarioMotivo = '', indicador = '', motivo = '' }) => {
    const baseLimpia = String(comentariosBase || '').trim();
    if (!baseLimpia) return comentarioMotivo;

    // Evita duplicar el mismo motivo cuando el primer guardado impacta pero falla la respuesta al cliente.
    const patronMotivo = new RegExp(`${escapeRegExp(indicador)}\\s*${escapeRegExp(motivo)}`, 'i');
    if (patronMotivo.test(baseLimpia)) {
        return baseLimpia;
    }

    return `${comentarioMotivo}\n\n${baseLimpia}`;
};

export const guardarMotivoComplementario = async ({
    idTramite,
    idCliente,
    idTipoTramite,
    idUsuario,
    versionFallback = 0,
    indicador,
    motivo,
    firma,
    comentariosFallback = ''
}) => {
    const comentarioMotivo = `${firma} ${indicador} ${String(motivo || '').trim()}`;
    let ultimoError = null;

    for (let intento = 0; intento < 2; intento += 1) {
        try {
            let tramiteActual = null;
            try {
                tramiteActual = await getTramitePorId(idTramite);
            } catch (_ignored) {
                tramiteActual = null;
            }

            const comentarioBase =
                (tramiteActual?.comentarios ?? '').trim() ||
                (tramiteActual?.comentario ?? '').trim() ||
                String(comentariosFallback || '').trim();

            const comentariosActualizados = componerComentariosConMotivo({
                comentariosBase: comentarioBase,
                comentarioMotivo,
                indicador,
                motivo: String(motivo || '').trim()
            });

            const tramiteActualizado = await modificarTramite(idTramite, {
                idCliente,
                idTipoTramite,
                idUsuario,
                version: tramiteActual?.version ?? versionFallback,
                comentarios: comentariosActualizados
            });

            return {
                tramiteActualizado,
                comentariosActualizados
            };
        } catch (error) {
            ultimoError = error;
            if (intento === 0 && esErrorReintentableConcurrencia(error)) {
                continue;
            }
            throw error;
        }
    }

    throw ultimoError;
};

// Cambiar el estado de un trámite
export const cambiarEstadoTramite = async (idTramite, idEstado, comentario = null, extras = {}) => {
    const payload = {
        idEstado,
        comentario: comentario || undefined,
        ...extras
    };
    const response = await apiClient.post(`${API_URL}/${idTramite}/estados`, payload);
    return response.data;
};

// Cancelar un trámite (baja lógica)
export const cancelarTramite = async (idTramite, motivo = null, observaciones = null) => {
    const data = {};
    if (motivo && motivo.trim()) data.motivo = motivo.trim();
    if (observaciones && observaciones.trim()) data.observaciones = observaciones.trim();
    const response = await apiClient.patch(`${API_URL}/${idTramite}/cancelar`, data);
    return response.data;
};

// Reactivar un trámite cancelado
export const reactivarTramite = async (idTramite, observaciones) => {
    const data = {
        observaciones: observaciones || "Reactivación sin observaciones adicionales"
    };
    const response = await apiClient.patch(`${API_URL}/${idTramite}/reactivar`, data);
    return response.data;
};

// Cancelar/reactivar "por ámbito": mismo botón que cancelarTramite/reactivarTramite (RTI/ART),
// para el resto de los módulos (Reajuste, Jubilación, Pensión, Ganancias, Minusvalía,
// Declaratoria, Plan de Pago, Reconocimiento, Amparo PNC, PNC, PUAM). No usan /cancelar ni
// /reactivar: esos son de RTI/ART y no se tocan (ver TramiteService.cancelarTramitePorAmbito).
export const cancelarTramitePorAmbito = async (idTramite, motivo = null, observaciones = null) => {
    const data = {};
    if (motivo && motivo.trim()) data.motivo = motivo.trim();
    if (observaciones && observaciones.trim()) data.observaciones = observaciones.trim();
    const response = await apiClient.patch(`${API_URL}/${idTramite}/cancelar-ambito`, data);
    return response.data;
};

export const reactivarTramitePorAmbito = async (idTramite, observaciones) => {
    const data = {
        observaciones: observaciones || "Reactivación sin observaciones adicionales"
    };
    const response = await apiClient.patch(`${API_URL}/${idTramite}/reactivar-ambito`, data);
    return response.data;
};

// Guardar nota/comentarios del trámite sin cambiar de estado
export const actualizarComentariosTramite = async (idTramite, comentarios) => {
    const response = await apiClient.patch(`${API_URL}/${idTramite}/comentarios`, { comentarios });
    return response.data;
};

// Eliminar un trámite por su ID
export const eliminarTramite = async (idTramite) => {
    const response = await apiClient.delete(`${API_URL}/${idTramite}`);
    return response.data;
};

/**
 * Las filas de planilla de las que salió el trámite ("Proviene de migración"). Lista vacía si no
 * vino de ninguna; pueden ser varias si un trámite existente se completó con otra planilla.
 */
export const getFilasPlanillaTramite = async (idTramite) => {
    const response = await apiClient.get(`${API_URL}/${idTramite}/filas-planilla`);
    return Array.isArray(response.data) ? response.data : [];
};
